import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Check, Building2, Users, Cpu, Target, Search } from 'lucide-react';

const FallbackDepartmentModal = ({ isOpen, onClose, onSelect, selectedValue, departments = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const fallbackDepartments = [
    {
      value: 'general',
      label: 'General Support',
      description: 'Handles general inquiries and basic support requests',
      icon: Building2,
      memberCount: 12,
      status: 'active'
    },
    {
      value: 'technical',
      label: 'Technical Support',
      description: 'Specialized technical assistance and complex issues',
      icon: Cpu,
      memberCount: 8,
      status: 'active'
    },
    {
      value: 'customer-service',
      label: 'Customer Service',
      description: 'Customer inquiries, billing, and account management',
      icon: Users,
      memberCount: 15,
      status: 'active'
    },
    {
      value: 'sales',
      label: 'Sales Support',
      description: 'Sales inquiries, product information, and lead management',
      icon: Target,
      memberCount: 6,
      status: 'active'
    }
  ];

  // Merge with actual departments if available
  const allDepartments = [
    ...fallbackDepartments,
    ...departments.map(dept => ({
      value: dept.id || dept.name?.toLowerCase().replace(/\s+/g, '-'),
      label: dept.name,
      description: dept.description || 'Department for handling specific requests',
      icon: dept.type === 'technical' ? Cpu : dept.type === 'customer-service' ? Users : Building2,
      memberCount: dept.memberCount || 0,
      status: dept.status || 'active'
    }))
  ];

  const filteredDepartments = allDepartments.filter(dept =>
    dept.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (value) => {
    onSelect(value);
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Fallback Department"
      size="lg"
    >
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search departments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Department Options */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredDepartments.map((department) => {
            const IconComponent = department.icon;
            const isSelected = selectedValue === department.value;
            
            return (
              <div
                key={department.value}
                onClick={() => handleSelect(department.value)}
                className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isSelected ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <IconComponent className={`w-5 h-5 ${
                      isSelected ? 'text-blue-600' : 'text-gray-600'
                    }`} />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{department.label}</h3>
                      {isSelected && (
                        <Check className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{department.description}</p>
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(department.status)}`}>
                        {department.status}
                      </span>
                      <span>{department.memberCount} members</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredDepartments.length === 0 && (
          <div className="text-center py-8">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No departments found</p>
            <p className="text-sm text-gray-500">Try adjusting your search terms</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default FallbackDepartmentModal;
